import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { KYCStatus } from '~/api/KYCStatus';
import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { VERIFICATION } from '~/constants/verification';
import { useModal } from '~/hooks/useModal';
import { useMounted } from '~/hooks/useMounted';
import useNavigateCheckout from '~/hooks/useNavigateCheckout';
import { cartStore, mainStore, orderStore, userStore } from '~/stores';
import { StartVerificationLocation } from '~/stores/MainStore';
import { checkoutBlocker } from '~/utils/checkoutBlocker';
import { formatPriceWithCurrency } from '~/utils/formaters';

interface CartButtonState {
  text: string;
  isHide?: boolean;
  additionalText?: string;
  isDisabled?: boolean;
  price?: string;
  onClick: () => void;
  isLoading?: boolean;
}

export const useCartButton = (): CartButtonState => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const mounted = useMounted();
  const isLoading =
    orderStore.isLoading ||
    orderStore.isETALoading ||
    cartStore.calculationProcess.isLoading;

  const handleNavigateToCheckout = useNavigateCheckout({
    source: 'BottomNavigationBar',
  });

  const handleNavigateToHome = () => {
    navigate('/');
  };

  const handleCheckout = () => {
    if (!userStore.isAuthorized) {
      openModal(ModalType.Auth);
      return;
    }

    if (
      userStore.isAuthorized &&
      userStore.personalData.kycStatus !== KYCStatus.Verified
    ) {
      mainStore.setStartVerificationLocation(StartVerificationLocation.CART);
      userStore
        .requestKYCSession(
          `${window.location.origin}${window.location.pathname}`,
        )
        .then(({ data }) => {
          const shouldOpenInAppBrowser =
            mainStore.isRN &&
            Number(mainStore.appVersion.replaceAll('.', '')) >= 1029;

          if (shouldOpenInAppBrowser) {
            mainStore.sendToRN('openInAppBrowser', { url: data.url });
          } else {
            window.location.href = data.url;
          }
        })
        .catch(() => void 0);
      return;
    }

    handleNavigateToCheckout();
  };

  const emptyCart: CartButtonState = {
    isHide: true,
    text: t('goShopping'),
    onClick: handleNavigateToHome,
  };

  /**
   * A blocked transition opens the address modal instead of navigating, so the
   * button says so and shows no spinner: the modal does not wait on the cart
   * recalculation. Labelled "Go to Checkout" and spinning through every
   * `calculate` round-trip, the same tap read as "it loads and then does
   * nothing". `mounted` gates the persisted read against the SSR markup.
   *
   * The bottom bar renders `price || additionalText`, so the hint under the
   * button only appears when the price is left off — which is right here: a
   * total for an order that cannot be placed is not the useful half.
   */
  const blocker = mounted ? checkoutBlocker() : null;

  const defaultCartState: CartButtonState =
    blocker === 'deliveryUnavailable'
      ? {
          text: t('checkout:deliveryUnavailable'),
          additionalText: t('checkout:deliveryUnavailableHint'),
          isDisabled: cartStore.selectedCartItems.length === 0,
          onClick: handleNavigateToCheckout,
        }
      : {
          text: blocker ? t('checkout:selectAddress') : t('goToCheckout'),
          isDisabled:
            cartStore.selectedCartItems.length === 0 || (isLoading && !blocker),
          price: formatPriceWithCurrency(
            cartStore.totalCartPrice.paidWithDiscount,
          ),
          isLoading: blocker ? false : isLoading,
          onClick: handleNavigateToCheckout,
        };

  if (!cartStore.cart.length) {
    return emptyCart;
  }

  if (cartStore.isMaxItemsPerOrderExceeded) {
    return {
      text: t('goToCheckout'),
      additionalText: t('maxItemsPerOrderError', {
        max: company.maxItemsPerOrder,
      }),
      isDisabled: true,
      onClick: () => void 0,
    };
  }

  if (!company.needVerification && !userStore.isAuthorized) {
    return {
      text: t('logInToCheckout'),
      additionalText: company.showCartAdditionalTextMobile
        ? t('phrases:verifyIdDescription')
        : undefined,
      isDisabled: false,
      onClick: handleCheckout,
    };
  }

  if (!company.needVerification && userStore.isAuthorized) {
    return defaultCartState;
  }

  switch (userStore.personalData.kycStatus) {
    case KYCStatus.Verified:
      return defaultCartState;

    case KYCStatus.InProgress:
    case KYCStatus.ResubmissionRequested:
    case KYCStatus.Started: {
      const statusConfig = VERIFICATION[userStore.personalData.kycStatus];
      const isDisabled =
        userStore.personalData.kycStatus === KYCStatus.InProgress;

      return {
        text: statusConfig.title,
        additionalText: statusConfig.description,
        isDisabled,
        onClick: handleCheckout,
      };
    }

    default:
      return {
        text: userStore.isAuthorized ? t('verifyId') : t('logInToCheckout'),
        additionalText: '',
        isDisabled: false,
        onClick: handleCheckout,
      };
  }
};
